import { useState } from 'react';

import DropdownArrowIcon from '@/assets/all-icons/dropdown-arrow-icon';
import InfoIconArtwork from '@/shared-components/icons/components/public/InfoIconArtwork';

import useDropdownPopover from './useDropdownPopover';

/**
 * Controlled form dropdown used when a field needs the form-control visual
 * contract but has selection rules beyond a plain select (multiple values,
 * maximum selections, or a custom option action).
 */
export default function FormDropdown({
  element,
  label,
  value,
  options,
  onChange,
  multiple = false,
  maxSelections,
  readOnly = false,
  previewMode = false,
  className = '',
  infoIcon,
  optionsHeader,
  formatValue,
  onOptionSelect,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isInfoIconOpen, setIsInfoIconOpen] = useState(false);
  const selectedValues = multiple ? (Array.isArray(value) ? value : []) : value;
  const {
    rootRef,
    triggerRef,
    menuId,
    close,
    toggle,
    handleTriggerKeyDown,
  } = useDropdownPopover({
    open: isOpen,
    onOpenChange: setIsOpen,
    disabled: readOnly,
    idPrefix: element || 'form-dropdown',
  });

  const getValue = (option) => (typeof option === 'object' ? option.value : option);
  const getLabel = (option) => (typeof option === 'object' ? option.label : option);
  const isSelected = (option) => (
    multiple ? selectedValues.includes(getValue(option)) : selectedValues === getValue(option)
  );

  const handleOptionSelect = (option) => {
    if (readOnly) return;

    if (onOptionSelect) {
      onOptionSelect(getValue(option));
      close();
      return;
    }

    if (multiple) {
      if (isSelected(option)) {
        onChange(selectedValues.filter((selectedOption) => selectedOption !== getValue(option)));
        return;
      }
      if (maxSelections && selectedValues.length >= maxSelections) return;
      onChange([...selectedValues, getValue(option)]);
      return;
    }

    onChange(getValue(option));
    close();
  };

  return (
    <div
      className={[
        'dropdown',
        'dropdown--form',
        'dropdown-form-select__container',
        multiple && 'dropdown-form-select__container--multiple',
        isOpen && 'is-open',
        readOnly && 'read-only',
        className,
      ].filter(Boolean).join(' ')}
      id={`${element}_error`}
      role="combobox"
      tabIndex={readOnly ? -1 : 0}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls={menuId}
      onClick={toggle}
      onKeyDown={handleTriggerKeyDown}
      ref={(node) => {
        rootRef.current = node;
        triggerRef.current = node;
      }}
    >
      <div className="dropdown__trigger dropdown-form-select__trigger">
        <div className="dropdown__text-container">
          <div className="dropdown__label dropdown-form-select__label">
            {label}
            {infoIcon && (
              <span
                role="button"
                tabIndex={-1}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  setIsInfoIconOpen((current) => !current);
                }}
              >
                <InfoIconArtwork
                  className="step-accordeon-step-header__info-icon"
                  open={isInfoIconOpen}
                  header={infoIcon.header || infoIcon.name}
                  description={infoIcon.description}
                  onClick={() => setIsInfoIconOpen(false)}
                />
              </span>
            )}
          </div>
          <div className="dropdown__value-row dropdown-form-select__value-row">
            <span className="dropdown__value dropdown-form-select__value">
              {formatValue ? formatValue(selectedValues) : selectedValues}
            </span>
          </div>
        </div>

        {!previewMode && (
          <DropdownArrowIcon className="dropdown__indicator dropdown-form-select__indicator" />
        )}
      </div>

      <div
        id={menuId}
        className="dropdown__menu dropdown-form-select__menu remove-scrollbar"
        role="listbox"
        aria-multiselectable={multiple || undefined}
      >
        {optionsHeader && <div className="dropdown-form-select__menu-header">{optionsHeader}</div>}
        {options.map((option) => {
          const selected = isSelected(option);
          const disabled = multiple && !selected && maxSelections && selectedValues.length >= maxSelections;
          return (
            <button
              key={getValue(option)}
              type="button"
              className={[
                'dropdown__option',
                'dropdown-form-select__option',
                selected && 'is-selected',
                disabled && 'is-disabled',
              ].filter(Boolean).join(' ')}
              role="option"
              aria-selected={selected}
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                handleOptionSelect(option);
              }}
            >
              {getLabel(option)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
